import { useSyncExternalStore } from 'react';

/**
 * Client-side accessibility preferences. They are applied as classes on
 * `<html>` so every component honours them without prop threading.
 *
 * Stored in localStorage because the backend has no per-user preference
 * endpoint — these are presentation tweaks, not account data.
 */
export type PreferenceKey = 'reduceMotion' | 'largeText' | 'strongFocus';

const STORAGE_KEY = 'retro:prefs';
const HTML_CLASS: Record<PreferenceKey, string> = {
  reduceMotion: 'pref-reduce-motion',
  largeText: 'pref-large-text',
  strongFocus: 'pref-strong-focus',
};

type Prefs = Record<PreferenceKey, boolean>;

function load(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Prefs>) : {};
    return {
      reduceMotion: Boolean(parsed.reduceMotion),
      largeText: Boolean(parsed.largeText),
      strongFocus: Boolean(parsed.strongFocus),
    };
  } catch {
    return { reduceMotion: false, largeText: false, strongFocus: false };
  }
}

let prefs: Prefs = load();
const listeners = new Set<() => void>();

function apply() {
  const root = document.documentElement;
  (Object.keys(HTML_CLASS) as PreferenceKey[]).forEach((key) => {
    root.classList.toggle(HTML_CLASS[key], prefs[key]);
  });
}

function set(key: PreferenceKey, value: boolean) {
  prefs = { ...prefs, [key]: value };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage-disabled: keep the in-memory value for this session.
  }
  apply();
  listeners.forEach((listener) => listener());
}

// Apply on boot so a reload does not flash the un-preferred styling.
apply();

export function usePreferences() {
  const value = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => prefs,
  );
  return { prefs: value, set };
}
